import { useEffect } from "react";
import { type FormAction } from "src/components/Form/Form.types.ts";
import type { Router } from "@remix-run/router";
import { useOpenModule } from "src/App/useOpenModule.ts";
import { getAdminTabsStore } from "src/admin/layout/tabs/global-tabs.store.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import { normalizePathname } from "src/lib/tabs/normalizePathname.ts";
import { useUserDataApi } from "src/user-data/useUserDataApi.ts";

type EventMap = {
  OPEN_MODULE: {
    resource: string;
    type?: FormAction;
    id?: string;
    urlParams?: Record<string, string>;
  };
  REMOVE_MAIN_TAB: {
    resource: string;
    requestId?: string;
    // id?: string;
  };
};

type Handlers = {
  [Key in keyof EventMap]: (params: EventMap[Key], requestId?: string) => void;
};

export const useWindowMessageEventListeners = ({
  router,
}: {
  router: Router;
}) => {
  const openModule = useOpenModule({
    router,
  });
  const { forceSavePendingUserData } = useUserDataApi();
  useEffect(() => {
    const removeMainTab = ({ resource }: { resource: string }) => {
      const store = getAdminTabsStore();

      const routePaths = createModuleRoutePaths({
        resource,
      });
      const id = normalizePathname(routePaths.root);

      // todo: add id property to TabDefinition for simpler connection between resource and its tab config
      const match = {
        route: {
          id: id,
          path: routePaths.root,
        },
        params: {},
        pathname: routePaths.root,
        pathnameBase: "/",
      };

      const config = store.config.find((tab) => {
        return tab.shouldOpen(match);
      });

      if (config) {
        const model = config.mapToUiModel(id, match, routePaths.root);
        const path = store.idToPathMap[id];
        store.setPaths((prevPaths) =>
          prevPaths.filter((prevPath) => prevPath !== path),
        );
        model.onClose?.();
      }
    };
    const handlers: Handlers = {
      OPEN_MODULE: (params) => {
        openModule(params);
      },
      REMOVE_MAIN_TAB: (params, requestId) => {
        removeMainTab(params);
        forceSavePendingUserData();
        if (requestId) {
          window.parent.postMessage(
            {
              requestId: requestId,
              payload: {
                success: true,
              },
            },
            "*",
          );
        }
      },
    };
    window.removeMainTab = removeMainTab;
    window.openModule = openModule;
    const cb = (event: MessageEvent<any>) => {
      if (typeof event.data !== "object" || event.data === null) return;
      const { eventName, payload = {}, requestId } = event.data || {};

      const handler = handlers[eventName as keyof EventMap];
      if (handler) {
        /* eslint-disable no-console */
        console.log("[useWindowEventListeners] event", {
          event,
        });
        handler(payload, requestId);
      }
    };

    window.addEventListener("message", cb);

    return () => {
      window.removeEventListener("message", cb);
    };
  }, [router, openModule, forceSavePendingUserData]);
};
